// src/utils/tableNormalizer.ts

export interface NormalizedTable {
  rows: any[];
  columns: string[];
}

/**
 * Converts ANY backend table shape into row-based data
 * Works for QC, normalization, scaling, etc.
 */
export const normalizeTableData = (input: any): NormalizedTable => {
  if (!input) return { rows: [], columns: [] };

  // 1️⃣ Already row-based
  if (Array.isArray(input) && input.every(r => r && typeof r === "object" && !Array.isArray(r))) {
    const columns = Array.from(new Set(input.flatMap(r => Object.keys(r))));
    return { rows: input, columns };
  }

  // 2️⃣ { columns, data }
  if (input.columns && Array.isArray(input.columns) && Array.isArray(input.data)) {
    const columns: string[] = input.columns;
    const rows = input.data.map((row: any[]) =>
      Object.fromEntries(columns.map((c, i) => [c, row?.[i]])),
    );
    return { rows, columns };
  }

  // 3️⃣ Column-oriented { colA: [], colB: [] }
  if (typeof input === "object" && !Array.isArray(input)) {
    const candidate =
      input.plotSample && typeof input.plotSample === "object"
        ? input.plotSample
        : input;

    const keys = Object.keys(candidate);
    const arrayKeys = keys.filter(k => Array.isArray(candidate[k]));

    if (arrayKeys.length) {
      const n = Math.max(...arrayKeys.map(k => candidate[k].length));
      const rows = Array.from({ length: n }, (_, i) => {
        const obj: any = {};
        keys.forEach(k => {
          obj[k] = Array.isArray(candidate[k]) ? candidate[k][i] : candidate[k];
        });
        return obj;
      });

      return { rows, columns: keys };
    }
  }

  // 4️⃣ Fallback
  if (typeof input === "object") {
    return { rows: [input], columns: Object.keys(input) };
  }

  return { rows: [{ value: input }], columns: ["value"] };
};
